import {
  PersonaIdTypeSplitter,
  PersonaTypeSingleLetter,
  VendableFull,
} from '@rabbit/data/types';
import { DR_Vendable, DR_Vendable_Private } from '@rabbit/data/portal';
import { t } from 'i18next';
import { useFirestoreQueryData } from '@react-query-firebase/firestore';
import { query, where } from 'firebase/firestore';

/**
 * Custom hook to fetch a single vendable's data from both public and private collections
 * using useFirestoreQueryData for real-time updates and built-in loading/error states
 *
 * @param vendableId - The document ID of the vendable to fetch
 * @param shouldRefetch - Optional refetch trigger
 * @param tenantLink
 * @returns Object containing vendableData, loading state, and error state
 */
export function useGetSingleVendable(
  vendableId: string | null | undefined,
  shouldRefetch?: number,
  tenantLink?: string
) {
  const resolvedTenantLink = tenantLink ?? t('tenantLink');
  // Query for public vendable data
  const vendablePublicQuery = vendableId
    ? query(
        DR_Vendable.collection(),
        where('docid', '==', vendableId),
        where(
          'mfr',
          '==',
          PersonaTypeSingleLetter.Manufacturer +
            PersonaIdTypeSplitter +
            resolvedTenantLink
        )
      )
    : undefined;

  const vendablePublicResult = useFirestoreQueryData(
    ['vendable-public', vendableId, shouldRefetch],
    vendablePublicQuery,
    {
      subscribe: true,
    },
    {
      enabled: !!vendableId,
    }
  );

  // Query for private vendable data
  const vendablePrivateQuery = vendableId
    ? query(
        DR_Vendable_Private.collection(),
        where('docid', '==', vendableId),
        where(
          'mfr',
          '==',
          PersonaTypeSingleLetter.Manufacturer +
            PersonaIdTypeSplitter +
            resolvedTenantLink
        )
      )
    : undefined;

  const vendablePrivateResult = useFirestoreQueryData(
    ['vendable-private', vendableId, shouldRefetch],
    vendablePrivateQuery,
    {
      subscribe: true,
    },
    {
      enabled: !!vendableId,
    }
  );

  // Extract data from results
  const vendablePublic = vendablePublicResult?.data?.[0] || null;
  const vendablePrivate = vendablePrivateResult?.data?.[0] || null;

  // Combine loading states - loading if either query is loading
  const loading =
    vendablePublicResult?.isLoading ||
    vendablePrivateResult?.isLoading ||
    false;

  // Combine error states - error if either query has an error
  const error = vendablePublicResult?.error || vendablePrivateResult?.error;

  // Combine the data into the expected VendableFull format
  const vendableData: VendableFull | null =
    vendablePublic && vendablePrivate
      ? { pub: vendablePublic, priv: vendablePrivate }
      : null;

  return {
    vendableData,
    loading,
    error,
  };
}
